import { useEffect, useRef, useState } from "react";
import { WidthProvider, Responsive, Layout } from "react-grid-layout";
import { IChart, Ilayout } from "../../../../@types/chart.type";
import { toast } from "react-toastify";
import { getPageHeight } from "../../utils";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { CompanyCapacity } from "../Widgets/CompanyCapacity";
import { UnavailableResources } from "../Widgets/UnavailableResources";
import { OverCostProjects } from "../Widgets/OverCostProjects";
import { ProjectVision } from "../Widgets/ProjectVision";
import { PositionCapacity } from "../Widgets/PositionCapacity";
import { CompanyUnavailableResources } from "../Widgets/CompanyUnavailableResources";
import { CompanyIdleness } from "../Widgets/CompanyIdleness";
import { ProjectsWithoutAllocations } from "../Widgets/ProjectsWithoutAllocations";
import { ProjectsWithoutABudget } from "../Widgets/ProjectsWithoutABudget";
import { CompanyTimeAllocationSummary } from "../Widgets/CompanyTimeAllocationSummary";
import { MyTeamIdleness } from "../Widgets/MyTeamIdleness";
import { CostPerMonthIdleness } from "../Widgets/CostPerMonthIdleness";
import { CostOfIdleness } from "../Widgets/CostOfIdleness";
import { TeamTimeAllocationSummary } from "../Widgets/TeamTimeAllocationSummary";
import styles from "./styles.module.scss";

interface Props {
  id?: string;
  name?: string;
  chartsFront: IChart[];
  setChartsFront: React.Dispatch<React.SetStateAction<IChart[]>>;
  listOfChartsToRemove: IChart[];
  setListOfChartsToRemove: React.Dispatch<React.SetStateAction<IChart[]>>;
  chartSelected: IChart;
  ismodeEdit: boolean;
}

const ResponsiveReactGridLayout = WidthProvider(Responsive);

export function DashboardLayout({
  chartSelected,
  ismodeEdit,
  setChartsFront,
  chartsFront,
  listOfChartsToRemove,
  setListOfChartsToRemove,
}: Props) {
  const [width, setWidth] = useState<number>(0);
  const [rowHeightGrid, setRowHeightGrid] = useState(0);
  const markingsRef = useRef<HTMLDivElement | null>(null);

  const gap = 36;

  useEffect(() => {
    if (markingsRef.current) {
      const alturaElemento = markingsRef.current.getBoundingClientRect().height;
      const alturaCalculada = alturaElemento / 2 - gap;

      setRowHeightGrid(Number(alturaCalculada.toFixed(1)));
    }
  }, [width]);

  const onWidthChange = (containerWidth: number) => {
    setWidth(containerWidth);
  };

  const onDrop = async (layout: Layout[], layoutItem: Layout) => {
    const itemExists = chartsFront.some(
      (chart) => chart?.layout?.i === chartSelected.widgetId
    );

    if (itemExists) {
      toast.error("Erro! Item duplicado.");
      return;
    }

    const newLayoutItem: Layout = {
      ...layoutItem,
      i: chartSelected.widgetId,
      w: chartSelected.size === "two" ? 2 : 1,
      h: 1,
    };

    const newChart = {
      ...chartSelected,
      layout: newLayoutItem,
    };

    const updatedCharts = [...chartsFront, newChart];

    const finalCharts = updatedCharts.map((chart) => {
      const matchingLayout = layout.find((l) => l.i === chart.layout.i);
      if (!matchingLayout) return chart;

      return {
        ...chart,
        layout: {
          ...chart.layout,
          ...matchingLayout,
        },
      };
    });

    setChartsFront(finalCharts);
  };

  const onDragStop = (layout: Layout[], oldItem: Layout, newItem: Layout) => {
    if (JSON.stringify(oldItem) === JSON.stringify(newItem)) return;

    const updatedCharts = chartsFront.map((chart) => {
      const matchingLayout = layout.find((l) => l.i === chart.layout.i);
      if (!matchingLayout) return chart;

      return {
        ...chart,
        layout: {
          ...chart.layout,
          ...matchingLayout,
        },
      };
    });

    setChartsFront(updatedCharts);
  };

  const getMinDimensions = (widgetId: string) => {
    switch (widgetId) {
      case "d9b2f5b8-6669-4b88-b5a8-f4e71ee2f25c":
      case "61891114-58c5-4738-99e1-5fee3a24ad8e":
      case "88f9da8b-8afc-400f-8434-5bcaa87f0949":
      case "e51ca84c-238d-4d5f-ac70-d5aae155be0c":
      case "efe64466-2c1c-4b27-ab15-3fbdaa88a07b":
      case "2630a5d3-fce9-49d4-92c0-b11ed3a4a6a0":
      case "d77c6007-8f75-4162-9e77-55513022083a":
      case "8cc546ea-d193-44a2-a7d9-1706660853b1":
      case "98416a47-d0e8-42dd-b982-cb57a7776b55":
      case "7a910f0f-360e-473a-ba2a-f2833fe3797c":
      case "09fe2a2b-0503-47e0-9313-b0784d1f34cd":
      case "7179f77b-515c-45bc-81a9-8f6a3a04d20f":
      case "29cf0eda-7a52-4105-afca-c2d1775186cf":
      case "3f5e1a7c-9b2d-4e4a-8f3c-72d6a1e5b4c9":
        return { minW: 2, minH: 1 };
      default:
        return { minW: 1, minH: 1 };
    }
  };

  const onResize = (layout: Ilayout[], oldItem: Ilayout, newItem: Ilayout) => {
    const dimensions = getMinDimensions(newItem.i);
    const minW = dimensions?.minW || 1;
    const minH = dimensions?.minH || 1;

    if (newItem.w < minW) {
      newItem.w = minW;
    }
    if (newItem.h < minH) {
      newItem.h = minH;
    }

    const updatedChart = chartsFront.map((chart) => {
      if (chart.layout.i === newItem.i) {
        return {
          ...chart,
          layout: {
            ...newItem,
            w: chart.size === "two" && newItem.w < minW ? minW : newItem.w,
          },
        };
      }
      return chart;
    });

    setChartsFront(updatedChart);
  };

  const deleteChart = async (idChart: string | undefined, widgetId: string) => {
    if (idChart) {
      const newListCharts = chartsFront.filter((chart) => chart.id !== idChart);

      setChartsFront(newListCharts);

      const chartToRemove = chartsFront.find((chart) => chart.id === idChart);

      if (chartToRemove) {
        const updatedList = [...listOfChartsToRemove, chartToRemove];

        const uniqueList = [
          ...new Map(updatedList.map((item) => [item.id, item])).values(),
        ];

        setListOfChartsToRemove(uniqueList);
      }
    } else {
      const newListCharts = chartsFront.filter(
        (chart) => chart.layout.i !== widgetId
      );
      setChartsFront(newListCharts);
    }
  };

  const renderWidget = (chart: IChart) => {
    const chartProps = { deleteChart, ismodeEdit, chart, rowHeightGrid };

    const widgetOptionsToRender: Record<string, React.ReactNode> = {
      "61891114-58c5-4738-99e1-5fee3a24ad8e": (
        <CompanyCapacity {...chartProps} />
      ),
      "88f9da8b-8afc-400f-8434-5bcaa87f0949": (
        <OverCostProjects {...chartProps} />
      ),
      "d9b2f5b8-6669-4b88-b5a8-f4e71ee2f25c": <ProjectVision {...chartProps} />,
      "2630a5d3-fce9-49d4-92c0-b11ed3a4a6a0": (
        <ProjectsWithoutABudget {...chartProps} />
      ),
      "3f5e1a7c-9b2d-4e4a-8f3c-72d6a1e5b4c9": (
        <ProjectsWithoutAllocations {...chartProps} />
      ),
      "8cc546ea-d193-44a2-a7d9-1706660853b1": (
        <MyTeamIdleness {...chartProps} />
      ),
      "98416a47-d0e8-42dd-b982-cb57a7776b55": (
        <CompanyIdleness {...chartProps} />
      ),
      "7a910f0f-360e-473a-ba2a-f2833fe3797c": (
        <CostPerMonthIdleness {...chartProps} />
      ),
      "29cf0eda-7a52-4105-afca-c2d1775186cf": (
        <CostOfIdleness {...chartProps} />
      ),
      "09fe2a2b-0503-47e0-9313-b0784d1f34cd": (
        <CompanyTimeAllocationSummary {...chartProps} />
      ),
      "7179f77b-515c-45bc-81a9-8f6a3a04d20f": (
        <TeamTimeAllocationSummary {...chartProps} />
      ),
      "d77c6007-8f75-4162-9e77-55513022083a": (
        <CompanyUnavailableResources {...chartProps} />
      ),
      "e51ca84c-238d-4d5f-ac70-d5aae155be0c": (
        <UnavailableResources {...chartProps} />
      ),
      "efe64466-2c1c-4b27-ab15-3fbdaa88a07b": (
        <PositionCapacity {...chartProps} />
      ),
    };

    return widgetOptionsToRender[chart.layout.i] || null;
  };

  return (
    <>
      <div style={{ height: getPageHeight() }} className={styles.dashboardPage}>
        <div
          style={{ height: getPageHeight(), gridAutoRows: rowHeightGrid }}
          className={`${styles.markings} ${ismodeEdit && styles.isModeEdit}`}
          ref={markingsRef}
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={`marker-${index}`} className={styles.dropMarker} />
          ))}
        </div>
        <ResponsiveReactGridLayout
          className={styles.gridLayout}
          style={{ minHeight: getPageHeight() }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 4, md: 4, sm: 3, xs: 2, xxs: 1 }}
          rowHeight={rowHeightGrid + 3}
          onDragStop={onDragStop}
          onDrop={onDrop}
          onResize={onResize}
          onWidthChange={onWidthChange}
          compactType={null}
          droppingItem={{
            w: chartSelected.size === "two" ? 2 : 1,
            h: 1,
            i: "__dropping-elem__",
          }}
          isDroppable={true}
          measureBeforeMount={false}
          margin={[22, 22]}
          containerPadding={[22, 22]}
          isResizable={ismodeEdit}
          isDraggable={ismodeEdit}
          useCSSTransforms={true}
        >
          {chartsFront.map((chart) => (
            <div
              style={{ height: `${rowHeightGrid}px` }}
              key={chart.layout.i}
              data-grid={chart.layout}
              className={`${ismodeEdit && styles.isModeEdit} ${styles.item}`}
            >
              {renderWidget(chart)}
            </div>
          ))}
        </ResponsiveReactGridLayout>
      </div>
    </>
  );
}
